import { useState, useEffect } from 'react';
import { Modal } from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormTextInput, FormSubmitButton, GlobalButton } from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { assetModelSchema, AssetModelSchema, defaultAssetModelValues, AssetModelApi } from '../models';
import { useAssetModelsActions, useAssetModels } from '../hooks';
import { useBrands } from '../../Brands/hooks';
import { BrandApi } from '../../Brands/models';
import { FormBrandAutocompleteInput } from '../../Brands/components';
import { Option } from '@/components/inputs/AutocompleteInput';

interface AddAssetModelModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (model: { id: string; name: string }) => void;
  brandId?: string;
}

const AddAssetModelModal = ({ open, onClose, onSuccess, brandId }: AddAssetModelModalProps) => {
  const [selectedBrand, setSelectedBrand] = useState<Option | null>(null);
  
  const { handleSubmit, control, reset, watch, setValue } = useForm<AssetModelSchema>({
    resolver: zodResolver(assetModelSchema),
    defaultValues: {
      ...defaultAssetModelValues,
      brandId: brandId || '',
    },
  });

  const { onCreateAssetModel, isCreatingAssetModel } = useAssetModelsActions();
  const { assetModels } = useAssetModels({ pageSize: 1000 });
  const { brands } = useBrands({ pageSize: 1000 });

  useEffect(() => {
    if (open && brandId) {
      const timer = setTimeout(() => {
        const brand = brands.find((b: BrandApi) => b.id === brandId);
        if (brand) {
          const brandOption: Option = {
            label: brand.name,
            data: brand,
          };
          setSelectedBrand(brandOption);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [open, brandId, brands]);

  const modelName = watch('name');
  const currentBrandId = watch('brandId');
  
  const existingModel = assetModels.find(
    (model: AssetModelApi) => 
      model.name.toLowerCase() === modelName?.toLowerCase() && 
      model.brandId === currentBrandId
  );

  const handleBrandSelect = (option: Option | null) => {
    setSelectedBrand(option);
    setValue('brandId', option?.data?.id || '', {
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (brandId && brands.length > 0) {
      const brand = brands.find((b: BrandApi) => b.id === brandId);
      if (brand && !selectedBrand) {
        const brandOption: Option = {
          label: brand.name,
          data: brand,
        };
        setSelectedBrand(brandOption);
        setValue('brandId', brand.id, {
          shouldValidate: true,
        });
      }
    }
  }, [brandId, brands, selectedBrand, setValue]);

  const onSubmit = async (data: AssetModelSchema) => {
    if (existingModel) {
      onSuccess({ id: existingModel.id, name: existingModel.name });
      onClose();
      return;
    }
    
    try {
      const result = await onCreateAssetModel(data, reset);
      onSuccess({ id: result.id, name: result.name });
      onClose();
    } catch (error) {
      console.error('Error creating asset model:', error);
    }
  };

  const handleClose = () => {
    reset();
    setSelectedBrand(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
        <div className='flex flex-col p-10 gap-4 w-[500px] bg-white outline-none rounded-sm shadow-xl'>
          <div className='flex justify-between items-center'>
            <h2 className='text-xl font-bold'>Agregar nuevo modelo</h2>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4'>
            <FormBrandAutocompleteInput
              name='brandId'
              placeholder='Seleccionar marca'
              control={control}
              value={selectedBrand}
              onChange={handleBrandSelect}
              onInputChange={() => {}}
              required
            />
            <FormTextInput
              name='name'
              placeholder='Nombre del modelo'
              control={control}
              autoFocus
              required
            />
            {existingModel && modelName && currentBrandId && (
              <div className='text-green-600 text-sm'>
                ✓ El modelo "{existingModel.name}" ya existe para esta marca y será seleccionado
              </div>
            )}
            <div className='flex gap-3 justify-end'>
              <GlobalButton
                variant={GlobalButtonVariant.secondaryButton}
                action={handleClose}
              >
                Cancelar
              </GlobalButton>
              <FormSubmitButton
                label='Agregar modelo'
                action={handleSubmit(onSubmit)}
                isLoading={isCreatingAssetModel}
              />
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default AddAssetModelModal; 